import axios, {
  AxiosInterceptorManager,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { toCamelKeyObject, toSnakeKeyObject } from "@/utils/case";
import { isErrorResponse } from "@/utils/use-fetcher";
import { clearTokens, getAccessToken, setTokens } from "@/utils/token-cookie";
import {
  endSession,
  isAccessTokenExpired,
  isAuthUrl,
  refreshAccessToken,
  toRefreshFailureError,
} from "@/utils/token-refresher";
import i18next from "i18next";
import { DateUtils } from "@/features/ui/utils/date-utils";
import { isSerializable } from "@/features/ui/app.type";

export interface ErrorResponse {
  error: ErrorPayload;
}

interface ErrorDetails {}

export interface LocalizedMessage extends ErrorDetails {
  "@type": "LocalizedMessage";
  locale: string;
  message: string;
}

export function isLocalizedMessage(obj: any): obj is LocalizedMessage {
  return (obj as LocalizedMessage).locale !== undefined;
}

export interface ErrorPayload {
  code: number;
  message: string;
  status: string;

  details?: ErrorDetails[];
}

export function isErrorPayload(obj: any): obj is ErrorPayload {
  const payload = obj as ErrorPayload;
  return payload.code !== undefined && payload.status !== undefined;
}

interface Pair<Data = any, Error = any> {
  resolve: (data: Data) => void;
  reject: (err: Error) => void;
}

export const client = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 30 * 1000,
  headers: {
    common: {
      "ngrok-skip-browser-warning": "1",
    },
  },
  paramsSerializer: (params) => {
    function foo(key: string, value: any): Record<string, any> {
      if (Array.isArray(value)) {
        return value.flatMap((i) => foo(key, i));
      } else if (value instanceof Date) {
        return [[key, DateUtils.toISOStringWithOffset(value)]];
      } else if (isSerializable(value)) {
        return [[key, value.serialize()]];
      } else if (typeof value === "object") {
        return [[key, JSON.stringify(value)]];
      } else {
        return [[key, value]];
      }
    }

    return Object.entries(params)
      .flatMap(([key, value]): Record<string, any> => {
        return foo(key, value);
      })
      .map((i) => {
        const [k, v] = i;
        return `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`;
      })
      .join("&");
  },
  formSerializer: {
    indexes: null,
  },
});

function isFileOrFiles(value: any) {
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.every((i) => i instanceof File);
    } else {
      return value instanceof File;
    }
  }
  return false;
}

function isDateOrDates(value: any) {
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.every((i) => i instanceof Date);
    } else {
      return value instanceof Date;
    }
  }
}

function isPlainObject(obj: any) {
  // Ensure obj is not null or undefined
  if (obj === null || typeof obj !== "object") {
    return false;
  }

  // Ensure obj's prototype is Object or null
  const proto = Object.getPrototypeOf(obj);
  return proto === Object.prototype || proto === null;
}

const requestInterceptor: Parameters<
  AxiosInterceptorManager<InternalAxiosRequestConfig<any>>["use"]
> = [
  async (config) => {
    // 만료됐으면 실패할 걸 알면서 보내지 않는다. 예전에는 이 검사가 multipart 분기
    // 안에만 있어서 파일 업로드에서만 동작했고, 나머지 요청은 항상 401 라운드트립을
    // 한 번 치른 뒤에야 갱신을 시작했다.
    if (!isAuthUrl(config.url) && isAccessTokenExpired()) {
      try {
        await refreshAccessToken();
      } catch (e) {
        throw toRefreshFailureError(e);
      }
    }

    if (config.data) {
      let data = config.data;
      // console.dir(data);
      // data = deleteUndefined(data);
      data = toSnakeKeyObject(data);

      if (config.headers["Content-Type"] === "multipart/form-data") {
        //data = deleteEmpty(data);

        if (typeof data === "object") {
          //multipart-form은 1depth만 검사한다
          Object.keys(data).forEach((key) => {
            const value = data[key];
            if (typeof value === "object") {
              if (Array.isArray(value)) {
                // if (
                //   value.every(isPlainObject) ||
                //   value.every((i) => Array.isArray(i))
                // ) {
                //   data[key] = value.map((i) => JSON.stringify(i));
                // }
                if (value.every((i) => Array.isArray(i))) {
                  data[key] = value.map((i) => JSON.stringify(i));
                } else {
                  data[key] = value.map((i) => {
                    if (isPlainObject(i)) {
                      return JSON.stringify(i);
                    } else {
                      return i;
                    }
                  });
                }
              } else if (isPlainObject(value)) {
                data[key] = JSON.stringify(value);
              }
            }
          });
        }
      }

      config.data = data;
    }

    if (config.params) {
      // if ("orderBy" in config.params) {
      //   config.params.orderBy = serializeOrderBy(config.params.orderBy);
      // }
      config.params = deleteUndefined(config.params);
      config.params = toSnakeKeyObject(config.params);
    }

    config.params = {
      ...config.params,
      locale: i18next.language,
    };

    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
];

client.interceptors.request.use(...requestInterceptor);

function deserializeDate(obj: any): typeof obj {
  const auditKeys = [
    "createdAt",
    "updatedAt",
    "issuedAt",
    "validUntil",
    "publishedAt",
    "issuedOn",
    "submittedAt",
    "organizedOn",
    "receivedAt",
    "finalEta",
  ];

  if (Array.isArray(obj)) {
    return obj.map((item) => deserializeDate(item));
  } else if (typeof obj === "object" && obj !== null) {
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const value = obj[key];
      if (auditKeys.indexOf(key) > -1 && value) {
        acc[key] = new Date(value);
      } else {
        acc[key] = deserializeDate(value);
      }
      return acc;
    }, {});
  }

  return obj;
}

function deleteUndefined(obj: any): typeof obj {
  if (Array.isArray(obj)) {
    return obj.map((item) => deleteUndefined(item));
  } else if (typeof obj === "object" && obj !== null) {
    if (obj instanceof Date || obj instanceof File || isSerializable(obj)) {
      return obj;
    }
    return Object.keys(obj).reduce((acc: any, key: string) => {
      const value = obj[key];
      if (value !== undefined) {
        acc[key] = deleteUndefined(obj[key]);
      }
      return acc;
    }, {});
  }

  return obj;
}

const responseInterceptor: Parameters<
  AxiosInterceptorManager<AxiosResponse<any>>["use"]
> = [
  (response) => {
    if (response.data) {
      response.data = toCamelKeyObject(response.data);
      response.data = deserializeDate(response.data);
    }

    // isTokenContainsUrl이 "/auth"를 매칭하므로 DELETE /auth(사인아웃)도 걸린다.
    // 순서를 뒤집지 않으면 사인아웃 응답으로 토큰을 덮어쓴다.
    if (isSignOut(response.config)) {
      clearTokens();
    } else if (isTokenContainsUrl(response.config.url)) {
      setTokens(response.data.accessToken, response.data.refreshToken);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const isRefreshable =
      status === 401 && originalRequest && !isAuthUrl(originalRequest.url);

    // 예전에는 응답 바디의 status가 JWT_TOKEN_EXPIRED / JWT_TOKEN_INVALIDATED일 때만
    // 갱신했다. 서버가 다른 문자열(UNAUTHENTICATED, 토큰 훼손 등)을 주면 갱신 시도조차
    // 하지 않고 401을 그대로 사용자에게 올렸다. 401이면 일단 한 번 갱신해 본다.
    if (isRefreshable && !originalRequest._retry) {
      // 재시도 표식을 갱신보다 먼저 세운다. 갱신이 성공했는데도 같은 401이 돌아오는
      // 경우(서버가 새 토큰을 안 준다든지)에 무한 루프가 되지 않게 한다.
      originalRequest._retry = true;
      try {
        await refreshAccessToken();
      } catch (e) {
        // terminal이면 JWT_TOKEN_REFRESH_FAILED로 바뀌어 세션이 정리되고,
        // transient(오프라인·타임아웃·5xx)면 전송 에러 그대로 올라간다.
        // 어느 쪽이든 원 요청의 낡은 401을 그대로 흘리지는 않는다.
        throw toRefreshFailureError(e);
      }
      return await client(originalRequest);
    }

    // 갱신하고 한 번 더 보냈는데도 401이면 더 해볼 게 없다. 세션을 끝낸다.
    // 이게 없으면 응답 바디의 status가 isCredentialError의 목록과 안 맞을 때
    // 로그아웃도 안 되고 401 토스트만 계속 뜬다.
    if (isRefreshable) {
      endSession();
    }

    if (isErrorResponse(error.response?.data)) {
      throw error.response.data;
    }

    throw error;
  },
];

client.interceptors.response.use(...responseInterceptor);

function isTokenContainsUrl(url: string | undefined) {
  // /auth/refresh는 여기에 없다. 갱신 응답은 token-refresher가 직접 기록한다.
  const candidates = ["/auth", "/auth/register"];
  return url && candidates.indexOf(url) > -1;
}

function isSignOut(config: AxiosRequestConfig) {
  return config.url === "/auth" && config.method === "delete";
}

function isAuthenticatedOnly(url: string) {
  const whiteList: (string | RegExp | ((url: string) => boolean))[] = [
    "/sign-up",
    "/sign-in",
    "/auth/refresh",
    "/auth",
  ];

  return !whiteList.find((i) => {
    if (typeof i === "string") {
      const expr = i.replace(/{[\w\d_-]+}/g, "[wd-_]+");
      return new RegExp(expr, "gi").test(url);
    } else if (i instanceof RegExp) {
      return i.test(url);
    } else if (typeof i === "function") {
      const func = i as (url: string) => boolean;
      return func(url);
    }
    return false;
  });
}
